import { HTMLAttributes } from 'react';
import { Table } from '@tanstack/react-table';
import {
  MdChevronLeft,
  MdChevronRight,
  MdKeyboardDoubleArrowLeft,
  MdKeyboardDoubleArrowRight,
} from 'react-icons/md';

import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';

interface IProps<TData> extends HTMLAttributes<HTMLDivElement> {
  table: Table<TData>;
  showSelectedCount?: boolean;
}

const DataTablePagination = <TData,>({
  table,
  showSelectedCount,
  ...props
}: IProps<TData>) => (
  <div
    {...props}
    className={cn(
      'flex min-w-0 flex-wrap items-center justify-between gap-4',
      props.className,
    )}
  >
    <div className="min-w-0 flex-1 text-sm text-muted-foreground">
      {showSelectedCount && (
        <>
          {table.getFilteredSelectedRowModel().rows.length} de{' '}
          {table.getFilteredRowModel().rows.length} linha(s) selecionada(s).
        </>
      )}
    </div>
    <div className="flex w-full flex-wrap items-center justify-between gap-3 sm:w-auto sm:justify-end lg:gap-8">
      <div className="flex items-center gap-2">
        <p className="text-sm font-medium">Linhas por página</p>
        <Select
          value={`${table.getState().pagination.pageSize}`}
          onValueChange={(value) => {
            table.setPageSize(Number(value));
          }}
        >
          <SelectTrigger className="h-8 w-[70px]">
            <SelectValue placeholder={table.getState().pagination.pageSize} />
          </SelectTrigger>
          <SelectContent side="top">
            {[10, 25, 50].map((pageSize) => (
              <SelectItem key={pageSize} value={`${pageSize}`}>
                {pageSize}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="flex items-center justify-center text-sm font-medium">
        Página {table.getState().pagination.pageIndex + 1} de{' '}
        {table.getPageCount()}
      </div>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          className="hidden h-8 w-8 p-0 lg:flex"
          onClick={() => table.setPageIndex(0)}
          disabled={!table.getCanPreviousPage()}
        >
          <span className="sr-only">Ir para primeira página</span>
          <MdKeyboardDoubleArrowLeft className="h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          className="h-8 w-8 p-0"
          onClick={() => table.previousPage()}
          disabled={!table.getCanPreviousPage()}
        >
          <span className="sr-only">Ir para página anterior</span>
          <MdChevronLeft className="h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          className="h-8 w-8 p-0"
          onClick={() => table.nextPage()}
          disabled={!table.getCanNextPage()}
        >
          <span className="sr-only">Ir para próxima página</span>
          <MdChevronRight className="h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          className="hidden h-8 w-8 p-0 lg:flex"
          onClick={() => table.setPageIndex(table.getPageCount() - 1)}
          disabled={!table.getCanNextPage()}
        >
          <span className="sr-only">Ir para ultima página</span>
          <MdKeyboardDoubleArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  </div>
);

export default DataTablePagination;
